import { useRef, useState } from 'react';
import { MAX_UPSTREAM_SKU_COST_MULTIPLIER } from '@nadoutong/contracts';
import type { UpstreamModelCostRecord, UpstreamModelCostWindowInput } from '@nadoutong/contracts';
import { createModelCostOperationId } from '../../api/upstreamModelCosts.ts';
import { useT } from '../../i18n/index.ts';
import { Button, Chip, Input } from '../../ui/index.ts';

/**
 * Inline procurement multiplier for one (channel × physical model)
 * identity (epic #1327 PR-5).
 *
 * Two invariants the surrounding table depends on:
 *
 * 1. **A missing row is not an implicit ×1.** The gateway prices from an
 *    exact snapshot and refuses to bind an unpriced target, so an empty
 *    field renders as "no price" rather than defaulting to one — showing
 *    a 1 here would read as "already priced" for a model that cannot be
 *    bound at all.
 * 2. **One operation id per edit, replayed across retries.** Both proxies
 *    mint a server-side id when the header is absent, which would make a
 *    retry after a network error a second, distinct write. The id is
 *    therefore minted when the draft first diverges and only cleared once
 *    the write is confirmed.
 */

export interface ModelCostIdentity {
  readonly capabilityId: string;
  readonly upstreamChannelId: string;
  readonly vendor: string;
  /** The physical model the channel buys — the cost row's key, not the SKU's. */
  readonly model: string;
  /** Display name for labels; may differ from `model` on a renamed row. */
  readonly label: string;
}

export interface ModelCostSaveInput {
  readonly identity: ModelCostIdentity;
  readonly costMultiplier: number;
  readonly operationId: string;
  /** Whole-set replacement. Omitted = leave windows untouched. */
  readonly windows?: readonly UpstreamModelCostWindowInput[];
}

function parseMultiplier(raw: string): number | null {
  if (raw.trim().length === 0) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 && parsed <= MAX_UPSTREAM_SKU_COST_MULTIPLIER
    ? parsed
    : null;
}

export function ModelCostCell({
  identity,
  cost,
  costsLoaded,
  saving,
  onSave,
}: {
  readonly identity: ModelCostIdentity;
  readonly cost: UpstreamModelCostRecord | undefined;
  /** False while the cost read is pending or failed — never rendered as unpriced. */
  readonly costsLoaded: boolean;
  readonly saving: boolean;
  readonly onSave: (input: ModelCostSaveInput) => void;
}): JSX.Element {
  const t = useT();
  const [draft, setDraft] = useState<string | null>(null);
  /**
   * The id is keyed by the value it was minted for, so retrying the same
   * edit replays one operation while changing the number starts a new one
   * — a different number is a different write, not a retry of this one.
   */
  const operationRef = useRef<{ value: string; id: string } | null>(null);

  const savedValue = cost === undefined ? '' : String(cost.cost_multiplier);
  const value = draft ?? savedValue;
  const parsed = parseMultiplier(value);
  const dirty = draft !== null && draft !== savedValue;
  const invalid = dirty && parsed === null;

  function handleChange(next: string): void {
    setDraft(next);
  }

  function handleSave(): void {
    if (!dirty || parsed === null) return;
    const current = operationRef.current;
    const operationId = current !== null && current.value === value
      ? current.id
      : createModelCostOperationId();
    operationRef.current = { value, id: operationId };
    onSave({ identity, costMultiplier: parsed, operationId });
  }

  return (
    <div className="flex flex-col items-start gap-1.5">
      <div className="flex items-center gap-2">
        <span className="text-label text-paper-low">×</span>
        <Input
          type="number"
          min="0"
          step="any"
          value={value}
          onChange={(event) => handleChange(event.currentTarget.value)}
          disabled={!costsLoaded}
          placeholder={t('upstream_detail.routing.cost.missing')}
          aria-label={t('upstream_detail.routing.cost.label', { model: identity.label })}
          invalid={invalid}
          mono
          className="w-24"
          data-testid={`upstream-model-cost-input-${identity.capabilityId}`}
        />
        <Button
          size="sm"
          onClick={handleSave}
          disabled={!dirty || parsed === null || saving}
          loading={saving}
          data-testid={`upstream-model-cost-save-${identity.capabilityId}`}
        >
          {saving ? t('upstream_detail.routing.cost.saving') : t('upstream_detail.routing.cost.save')}
        </Button>
      </div>
      <span data-testid={`upstream-model-cost-state-${identity.capabilityId}`}>
        {!costsLoaded ? (
          <Chip tone="neutral">{t('upstream_detail.routing.cost.unavailable')}</Chip>
        ) : cost === undefined ? (
          <Chip tone="warn">{t('upstream_detail.routing.cost.missing')}</Chip>
        ) : (
          <Chip tone="ok">
            {t('upstream_detail.routing.cost.explicit', { value: cost.cost_multiplier })}
          </Chip>
        )}
      </span>
      {invalid ? (
        <span className="text-micro text-bad" role="alert">
          {t('upstream_detail.routing.cost.invalid', { max: MAX_UPSTREAM_SKU_COST_MULTIPLIER })}
        </span>
      ) : null}
    </div>
  );
}
